// src/context/AuthContext.tsx - Enhanced version with API integration and token refresh
import React, { createContext, useContext, ReactNode, useState, useEffect, useRef } from 'react';

// Безопасные функции для работы с localStorage
const safelySetItem = (key: string, value: string | null): void => {
  try {
    if (value && value !== 'undefined') {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error(`Error setting localStorage item ${key}:`, e);
  }
};

const safelyGetItem = (key: string): string | null => {
  try {
    const value = localStorage.getItem(key);
    return value === 'undefined' ? null : value;
  } catch (e) {
    console.error(`Error getting localStorage item ${key}:`, e);
    return null;
  }
};

const safelyRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error(`Error removing localStorage item ${key}:`, e);
  }
};

// Interfaces for API response types
interface AuthResponse {
  access?: string;
  refresh?: string;
  success?: boolean;
  id?: number;
  [key: string]: any;
}

interface UserProfileResponse extends AuthUser {
  [key: string]: any;
}

// Обновляем интерфейс AuthUser, добавляя недостающие свойства
interface AuthUser {
  id: number;
  email: string;
  name: string;
  usage_plan?: 'trial' | 'basic' | 'pro' | 'premium'; // Добавляем для SubscriptionForm
  usage_balance?: number; // Добавляем для SubscriptionForm
  is_active?: boolean; // Добавляем для SubscriptionForm
  business_id?: number;
  business_type?: string;
  business_currency?: string;
}

interface RegistrationData {
  email: string;
  password: string;
  // Полностью убраны поля name, phone и business_type
}

// Simple API service implementation
const api = {
  // API endpoint definitions
  endpoints: {
    auth: {
      login: '/api/token/',
      register: '/api/business/',
      me: '/api/business/profile/',
      profile: '/api/business/profile/',
      facebookOAuth: '/api/accounts/facebook/login/',
      googleOAuth: '/api/accounts/google/login/',
      socialAccounts: '/api/accounts/social/',
      unlinkSocial: '/api/accounts/unlink/',
      refreshToken: '/api/token/refresh/'
    },
    social: {
      facebook: '/api/accounts/facebook/login/',
      google: '/api/accounts/google/login/'
    }
  },
  
  // API request methods
  async get(url: string): Promise<UserProfileResponse> {
    try {
      console.log(`API GET request to: ${url}`);
      const token = safelyGetItem('accessToken');
      console.log(`Token available for request: ${!!token}`);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      if (!response.ok) {
        console.error(`API GET error: Status ${response.status} for URL ${url}`);
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json() as UserProfileResponse;
      console.log(`API GET success: ${url}`, { hasData: !!data, userId: data?.id });
      return data;
    } catch (error) {
      console.error('API GET error:', error);
      throw error;
    }
  },
  
  async post(url: string, data: any): Promise<AuthResponse> {
    try {
      console.log(`API POST request to: ${url}`, { 
        dataKeys: Object.keys(data),
        isAuthRequest: url.includes('token')
      });
      const token = safelyGetItem('accessToken');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`API POST error: Status ${response.status} for URL ${url}`, { errorBody });
        throw new Error(`HTTP error! Status: ${response.status}, Body: ${errorBody}`);
      }
      
      const responseData = await response.json() as AuthResponse;
      console.log(`API POST success: ${url}`, { 
        hasAccessToken: !!responseData.access,
        hasRefreshToken: !!responseData.refresh,
        hasId: !!responseData.id
      });
      return responseData;
    } catch (error) {
      console.error('API POST error:', error);
      throw error;
    }
  }
};

interface AuthContextValue {
  isAuthenticated: boolean;
  user: AuthUser | null;
  isLoading: boolean;
  error: {
    auth?: string | null;
    api?: string | null;
  };
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: RegistrationData) => Promise<boolean>;
  loginWithFacebook: () => void;
  loginWithGoogle: () => void;
  getSocialAccounts: () => Promise<any>;
  unlinkSocialAccount: (provider: string) => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
  requestPasswordReset: (email: string, recaptchaToken: string) => Promise<boolean>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
}

// Create context with initial values
const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: {},
  login: async () => false,
  logout: () => {},
  register: async () => false,
  loginWithFacebook: () => {},
  loginWithGoogle: () => {},
  getSocialAccounts: async () => ({}),
  unlinkSocialAccount: async () => false,
  checkAuth: async () => false,
  refreshToken: async () => false,
  requestPasswordReset: async () => false,
  resetPassword: async () => false
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState({
    isAuthenticated: false,
    user: null as AuthUser | null,
    isLoading: true, // Start with loading state
    error: {
      auth: null as string | null,
      api: null as string | null
    }
  });
  
  // Используем useRef для отслеживания запросов в процессе
  const registrationInProgressRef = useRef(false);
  const loginInProgressRef = useRef(false);

  // Refresh token function implementation - без моковых данных
  const refreshToken = async (): Promise<boolean> => {
    console.log("====== TOKEN REFRESH STARTED ======");
    console.log("Attempting to refresh token");
    const refreshTokenValue = safelyGetItem('refreshToken');
    console.log("Refresh token available:", !!refreshTokenValue);
    
    if (!refreshTokenValue) {
      console.error("No refresh token available");
      setState(prev => ({
        ...prev,
        error: {
          ...prev.error,
          auth: 'Ошибка обновления токена: токен отсутствует'
        }
      }));
      return false;
    }
    
    try {
      const response = await api.post(api.endpoints.auth.refreshToken, {
        refresh: refreshTokenValue
      });
      
      if (response && response.access) {
        // Save the new token
        if (response.access && response.access !== 'undefined') {
          safelySetItem('accessToken', response.access);
        }
        
        // If there's a new refresh token, save it too
        if (response.refresh) {
          safelySetItem('refreshToken', response.refresh);
        }
        
        // Validate the new token by getting user data
        await checkAuth();
        console.log("Token refreshed successfully");
        return true;
      } else {
        console.error("Token refresh failed: Invalid response");
        setState(prev => ({
          ...prev,
          error: {
            ...prev.error,
            auth: 'Ошибка обновления токена: некорректный ответ'
          }
        }));
        return false;
      }
    } catch (error: any) {
      console.error("Token refresh error:", error);
      setState(prev => ({
        ...prev,
        error: {
          ...prev.error,
          auth: 'Ошибка обновления токена',
          api: error.message || null
        }
      }));
      return false;
    }
  };

// Add initialization effect with new token refresh functionality
useEffect(() => {
  console.log("AuthProvider initializing...");
  console.log("Token check process starting...");
  let isMounted = true;
  
  // Сразу проверяем наличие токена в начале функции
  const hasToken = safelyGetItem('accessToken');
  console.log("Token exists:", !!hasToken);
  
  // Если токена нет, сразу устанавливаем состояние неавторизованного пользователя и выходим
  if (!hasToken) {
    console.log("No token found, immediately setting unauthenticated state without API requests");
    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: {
        auth: null,
        api: null
      }
    }));
    return; // Выходим из эффекта без дальнейших проверок и запросов к API
  }
  
  // Check token and attempt to get user profile - только если токен есть
  const checkToken = async () => {
    try {
      console.log("Found existing token, checking authentication");
      try {
        // Try to get user profile
        console.log("Making API request to:", api.endpoints.auth.profile);
        const userProfile = await api.get(api.endpoints.auth.profile);
        console.log("Profile response received:", !!userProfile);
        
        if (userProfile && userProfile.id && isMounted) {
          setState(prev => ({
            ...prev,
            isAuthenticated: true,
            user: userProfile,
            isLoading: false,
            error: {
              auth: null,
              api: null
            }
          }));
        }
      } catch (profileError) {
        console.error("Error fetching user profile:", profileError);
        
        // Try to refresh the token
        if (isMounted) {
          try {
            const refreshSuccessful = await refreshToken();
            
            if (!refreshSuccessful && isMounted) {
              console.log("Token refresh failed, logging out");
              logout();
            }
          } catch (refreshError) {
            console.error("Error refreshing token:", refreshError);
            if (isMounted) {
              logout();
              setState(prev => ({
                ...prev,
                error: {
                  ...prev.error,
                  auth: 'Ошибка обновления токена'
                }
              }));
            }
          }
        }
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
      if (isMounted) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: {
            ...prev.error,
            auth: 'Failed to initialize authentication'
          }
        }));
      }
    }
  };
  
  // Primary timeout to ensure we don't block indefinitely - только если токен есть
  const primaryTimeoutId = setTimeout(() => {
    if (isMounted && state.isLoading) {
      console.warn("Auth initialization timeout - forcing completion");
      setState(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  }, 6000); // Увеличено с 3000 до 6000 мс
  
  // Emergency backup timeout - только если токен есть
  const emergencyTimeoutId = setTimeout(() => {
    if (isMounted && state.isLoading) {
      console.error("EMERGENCY: Auth still loading after extended time - forcing exit");
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: {
          ...prev.error,
          auth: 'Authentication timeout'
        }
      }));
    }
  }, 10000); // Увеличено с 5000 до 10000 мс
  
  // Вызываем checkToken только если токен есть
  checkToken();
  
  // Cleanup function
  return () => {
    isMounted = false;
    clearTimeout(primaryTimeoutId);
    clearTimeout(emergencyTimeoutId);
  };
}, []);

  // Login function with real API integration
  // Обновленная функция login для AuthProvider.tsx
// Обновленная функция login для AuthContext.tsx
const login = async (email: string, password: string): Promise<boolean> => {
  console.log("====== LOGIN PROCESS STARTED ======");
  // Предотвращаем множественные запросы
  if (loginInProgressRef.current) {
    console.warn("Login already in progress, ignoring duplicate request");
    return false;
  }
  
  // Устанавливаем флаг, что авторизация в процессе
  loginInProgressRef.current = true;
  console.log("Login flag set to prevent duplicate requests");
  
  try {
    setState(prev => ({ 
      ...prev, 
      isLoading: true, 
      error: { ...prev.error, auth: null, api: null } 
    }));
    console.log("Login attempt with:", email);
    
    // Добавляем таймаут с более подробным сообщением об ошибке
    const loginTimeout = setTimeout(() => {
      console.warn("Login request is taking longer than expected");
    }, 5000);
    
    // Используем email, как указано в кастомном сериализаторе
    const response = await api.post(api.endpoints.auth.login, {
      email: email,
      password
    });
    
    // Очищаем таймаут после получения ответа
    clearTimeout(loginTimeout);
    
    if (response.access) {
      safelySetItem('accessToken', response.access);
      if (response.refresh) {
        safelySetItem('refreshToken', response.refresh);
      }
      
      // Получаем данные пользователя с дополнительным логированием
      console.log("Login successful, fetching user data");
      try {
        await checkAuth();
        
        // Обновляем навигацию - перенаправляем на страницу личного кабинета
        if (typeof window !== 'undefined') {
          // @ts-ignore
          // Уведомляем о успешной авторизации через пользовательское событие
          const authEvent = new CustomEvent('auth:login:success');
          // @ts-ignore
          window.dispatchEvent(authEvent);
          
          // Перенаправляем на страницу личного кабинета
          // @ts-ignore
          const basename = window.location.pathname.includes('/rinnax-app') ? '/rinnax-app' : '';
          // @ts-ignore
          window.history.pushState({}, '', `${basename}/personal`);
        }
      } catch (profileError) {
        console.error("Error fetching user profile after login:", profileError);
        // Пропускаем ошибку получения профиля, так как авторизация уже успешна
      }
      
      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        isLoading: false
      }));
      
      console.log("Login completed successfully");
      loginInProgressRef.current = false; // Сбрасываем флаг
      return true;
    }
    
    setState(prev => ({ 
      ...prev, 
      isLoading: false, 
      error: { 
        ...prev.error,
        auth: 'Неверные учетные данные' 
      } 
    }));
    console.error("Login failed: Invalid credentials or unexpected response");
    loginInProgressRef.current = false; // Сбрасываем флаг
    return false;
  } catch (err: any) {
    console.error('Login error details:', err);
    
    // Улучшенная обработка ошибок для более понятных сообщений
    let errorMessage = 'Не удалось выполнить вход';
    let apiDetails = null;
    
    // Проверка на таймаут
    if (err.message && err.message.includes('timeout')) {
      errorMessage = 'Превышено время ожидания ответа от сервера. Пожалуйста, проверьте подключение к интернету и попробуйте снова.';
    } else if (err.message && err.message.includes('401')) {
      errorMessage = 'Неверный логин или пароль';
      apiDetails = err.message;
    } else if (err.message && err.message.includes('Network Error')) {
      errorMessage = 'Проблема с подключением к серверу';
      apiDetails = 'Проверьте интернет-соединение';
    } else if (err.message) {
      apiDetails = err.message;
    }
    
    // Обработка деталей API ошибки
    if (err.response) {
      try {
        // Более детальное извлечение информации об ошибке
        const errorData = err.response.data;
        const statusCode = err.response.status;
        
        if (statusCode === 401) {
          errorMessage = 'Неверный логин или пароль';
        } else if (statusCode === 400) {
          if (typeof errorData === 'object') {
            const errorDetails = Object.entries(errorData)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
            apiDetails = errorDetails;
          }
        }
        
        apiDetails = apiDetails || (typeof errorData === 'object' 
          ? JSON.stringify(errorData) 
          : `Код ошибки: ${statusCode}, ${String(errorData)}`);
      } catch (parseError: any) {
        apiDetails = `Ошибка при обработке ответа: ${parseError.message}`;
      }
    }
    
    setState(prev => ({ 
      ...prev, 
      isLoading: false, 
      error: { 
        ...prev.error,
        auth: errorMessage,
        api: apiDetails
      } 
    }));
    
    loginInProgressRef.current = false; // Сбрасываем флаг
    return false;
  }
};

const logout = async () => {
  console.log("====== LOGOUT PROCESS STARTED ======");
  console.log("Logging out user");
  console.log("Auth state before logout:", {
    isAuthenticated: state.isAuthenticated,
    hasUser: !!state.user,
    userId: state.user?.id
  });
  
  try {
    // 1. Отправляем запрос на бэкенд для аннулирования токена
    const token = safelyGetItem('accessToken');
    if (token) {
      try {
        // Вызываем API для аннулирования токена на сервере
        await fetch('/api/auth/logout/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        console.log("Token blacklisted on server");
      } catch (e) {
        console.error("Error blacklisting token:", e);
        // Продолжаем процесс выхода даже если запрос не удался
      }
    }
        
    // 2. Очищаем все данные из localStorage
    safelyRemoveItem('accessToken');
    safelyRemoveItem('refreshToken');
    
    // 3. Очищаем все данные из sessionStorage, если используются
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
    
    // 4. Сбрасываем состояние контекста аутентификации
    setState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: {
        auth: null,
        api: null
      }
    });
    
    // 5. Вызываем событие для оповещения других компонентов о выходе
    if (typeof window !== 'undefined') {
      // @ts-ignore
      const logoutEvent = new CustomEvent('auth:logout:success');
      // @ts-ignore
      window.dispatchEvent(logoutEvent);
    }
    
    console.log("User successfully logged out");
    
  } catch (error) {
    console.error("Logout error:", error);
    // В случае ошибки, всё равно очищаем локальное состояние
    setState({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: {
        auth: null,
        api: null
      }
    });
  }
};

// Only the modified register function from AuthProvider.tsx
const register = async (userData: RegistrationData): Promise<boolean> => {
  console.log("====== REGISTRATION PROCESS STARTED ======");
  // Prevent multiple requests
  if (registrationInProgressRef.current) {
    console.warn("Registration already in progress, ignoring duplicate request");
    return false;
  }
  
  // Set flag that registration is in progress
  registrationInProgressRef.current = true;
  console.log("Registration flag set to prevent duplicate requests");
  
  // Hide password in logs
  console.log("Registration attempt with:", {
    ...userData,
    password: userData.password ? '********' : null
  });
  
  setState(prev => ({ 
    ...prev, 
    isLoading: true, 
    error: { ...prev.error, auth: null, api: null } 
  }));
  
  try {
    // Make sure required fields are filled
    if (!userData.email || !userData.password) {
      console.error("Missing required fields for registration");
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: { 
          ...prev.error,
          auth: 'Не заполнены обязательные поля' 
        }
      }));
      registrationInProgressRef.current = false; // Reset flag
      return false;
    }
    
    // Use exactly the fields expected by the backend
    const enhancedUserData = {
      email: userData.email,
      password: userData.password,
      name: userData.email.split('@')[0], // Use part of email as name
      business_type: 'none'  // Default value for business type
    };
    
    console.log("Sending registration data with fields:", Object.keys(enhancedUserData));
    
    // API registration request
    const response = await api.post(api.endpoints.auth.register, enhancedUserData);
    console.log("Registration response received:", { 
      success: !!response.id,
      id: response.id
    });
    
    if (response.id) {
      console.log("Registration successful, ID:", response.id);
      
      // Try immediate login with new credentials
      console.log("Attempting auto-login after successful registration");
      try {
        const loginSuccess = await login(userData.email, userData.password);
        if (loginSuccess) {
          console.log("Auto-login successful");
        } else {
          console.warn("Auto-login failed after successful registration");
        }
      } catch (loginError) {
        console.error("Error during auto-login after registration:", loginError);
        // Continue even if auto-login failed
      }
      
      setState(prev => ({ 
        ...prev, 
        isLoading: false 
      }));
      
      registrationInProgressRef.current = false; // Reset flag
      return true;
    }
    
    console.error("Registration failed, no ID returned");
    setState(prev => ({ 
      ...prev, 
      isLoading: false, 
      error: { 
        ...prev.error,
        auth: 'Ошибка при регистрации' 
      }
    }));
    
    registrationInProgressRef.current = false; // Reset flag
    return false;
  } catch (err: any) {
    console.error('Registration error:', err);
    
    // Enhanced error handling with user-friendly messages
    let errorMessage = 'Не удалось завершить регистрацию';
    let apiDetails = null;
    
    // Try to parse response body for more detailed error
    if (err.message && typeof err.message === 'string') {
      if (err.message.includes('уже существует') || 
          err.message.includes('already exists') || 
          err.message.includes('такой email')) {
        errorMessage = 'Email уже зарегистрирован';
        apiDetails = 'Пользователь с таким email уже существует';
      } else if (err.message.includes('password')) {
        errorMessage = 'Проблема с паролем';
        apiDetails = 'Проверьте требования к паролю';
      } else if (err.message.includes('timeout') || err.message.includes('Network Error')) {
        errorMessage = 'Проблема с подключением';
        apiDetails = 'Проверьте интернет-соединение';
      } else if (err.message.includes('400')) {
        errorMessage = 'Ошибка в отправленных данных';
        apiDetails = err.message;
      } else {
        // Keep technical details for debugging but don't expose them to users
        apiDetails = err.message;
      }
    }
    
    setState(prev => ({
      ...prev,
      isLoading: false,
      error: {
        ...prev.error,
        auth: errorMessage,
        api: apiDetails
      }
    }));
    
    registrationInProgressRef.current = false; // Reset flag
    return false;
  }
};

  const loginWithFacebook = () => {
    console.log('Facebook login attempt');
    try {
      // Check if window is defined (for SSR compatibility)
      if (typeof window !== 'undefined') {
        // @ts-ignore
        window.location.href = api.endpoints.social.facebook;
      }
    } catch (error) {
      console.error("Facebook login error:", error);
      setState(prev => ({
        ...prev,
        error: {
          ...prev.error,
          auth: 'Ошибка входа через Facebook'
        }
      }));
    }
  };

  const loginWithGoogle = () => {
    console.log('Google login attempt');
    try {
      // Check if window is defined (for SSR compatibility)
      if (typeof window !== 'undefined') {
        // @ts-ignore
        window.location.href = api.endpoints.social.google;
      }
    } catch (error) {
      console.error("Google login error:", error);
      setState(prev => ({
        ...prev,
        error: {
          ...prev.error,
          auth: 'Ошибка входа через Google'
        }
      }));
    }
  };

  const getSocialAccounts = async () => {
    console.log("Fetching social accounts");
    try {
      const response = await api.get(api.endpoints.auth.socialAccounts);
      return response;
    } catch (error) {
      console.error("Error fetching social accounts:", error);
      return { social_accounts: [] };
    }
  };

  const unlinkSocialAccount = async (provider: string) => {
    console.log(`Unlinking ${provider} account`);
    try {
      const response = await api.post(api.endpoints.auth.unlinkSocial, { provider });
      return !!response.success;
    } catch (error) {
      console.error(`Error unlinking ${provider} account:`, error);
      return false;
    }
  };

// Обновляем функцию checkAuth в AuthContext.tsx
const checkAuth = async (): Promise<boolean> => {
  console.log("====== CHECK AUTH STARTED ======");
  console.log("Checking authentication status");
  const token = safelyGetItem('accessToken');
  console.log("Access token available:", !!token);
  
  if (!token) {
    setState(prev => ({ 
      ...prev, 
      isAuthenticated: false, 
      user: null, 
      isLoading: false,
      error: {
        ...prev.error,
        auth: 'No authentication token'
      }
    }));
    return false;
  }
  
  try {
    // Try the /api/business/profile/ endpoint first
    try {
      console.log("Attempting to get user profile from:", api.endpoints.auth.me);
      const userData = await api.get(api.endpoints.auth.me);
      
      if (userData && userData.id) {
        console.log("Получен профиль пользователя:", {
          id: userData.id,
          email: userData.email,
          name: userData.name,
          business_id: userData.business_id,
          business_type: userData.business_type,
          business_currency: userData.business_currency  // Логируем валюту
        });
        
        // Сохраняем валюту в localStorage
        if (userData.business_currency) {
        safelySetItem('businessCurrency', userData.business_currency);
        }

        // Обновляем состояние пользователя только если получены данные
        setState({
          isAuthenticated: true,
          user: userData,
          isLoading: false,
          error: {
            auth: null,
            api: null
          }
        });
        
        return true;
      }
    } catch (profileError) {
      console.log("Profile endpoint failed, trying fallback to specific ID:", profileError);
      
      // Fallback: Try to get user by ID from token
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        // @ts-ignore
        const payload = JSON.parse(window.atob(base64));
        const userId = payload.user_id || payload.id;
        
        if (userId) {
          const userData = await api.get(`/api/business/${userId}/`);
          
          if (userData && userData.id) {
            console.log("Получен профиль пользователя по ID:", userData);
            
            setState({
              isAuthenticated: true,
              user: userData,
              isLoading: false,
              error: {
                auth: null,
                api: null
              }
            });
            return true;
          }
        }
      } catch (idError) {
        console.error("Failed to fetch user by ID:", idError);
      }
    }
    
    // If we get here, both attempts failed - try refresh
    console.log("Не удалось получить профиль, пробуем обновить токен");
    const refreshSuccess = await refreshToken();
    if (refreshSuccess) {
      return true;
    }
    
    // If refresh fails, log out
    console.log("Обновление токена не удалось, выходим из системы");
    logout();
    return false;
  } catch (error: any) {
    console.error("Auth validation error:", error);
    
    // Try to refresh token on 401 errors
    if (error.response?.status === 401 || error.message?.includes('401')) {
      try {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return true;
        }
      } catch (refreshError) {
        console.error("Error refreshing token:", refreshError);
      }
      
      logout();
    }
    
    setState(prev => ({
      ...prev,
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: {
        auth: 'Session expired',
        api: error.message || null
      }
    }));
    
    return false;
  }
};

const requestPasswordReset = async (email: string, recaptchaToken: string): Promise<boolean> => {
  try {
    const response = await fetch('/api/password/reset-request/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, recaptcha: recaptchaToken }),
    });
    
    return response.ok;
  } catch (error) {
    console.error('Password reset request error:', error);
    return false;
  }
};

const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
  try {
    const response = await fetch(`/api/password/reset-confirm/${token}/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password: newPassword }),
    });
    
    return response.ok;
  } catch (error) {
    console.error('Password reset error:', error);
    return false;
  }
};

  const contextValue: AuthContextValue = {
    ...state,
    login,
    logout,
    register,
    loginWithFacebook,
    loginWithGoogle,
    getSocialAccounts,
    unlinkSocialAccount,
    checkAuth,
    refreshToken,
    requestPasswordReset,
    resetPassword
  };

  // Логирование для отладки - расширенная информация об AuthProvider
  useEffect(() => {
    console.group('🔍 AuthProvider Debug Info');
    console.log('Current state:', state);
    console.log('localStorage tokens:', {
      accessToken: safelyGetItem('accessToken')?.substring(0, 10) + '...',
      refreshTokenPresent: !!safelyGetItem('refreshToken')
    });
    
    if (typeof window !== 'undefined') {
      // @ts-ignore
      console.log('URL:', window.location.href);
      console.log('User agent:', navigator.userAgent);
      console.log('Window localStorage available:', !!window.localStorage);
    }
    console.groupEnd();
    
    return () => {
      console.log('🧹 AuthProvider unmounted');
    };
  }, [state]);

  // Важно: всегда рендерим дочерние компоненты, даже если аутентификация все еще загружается
  // Это предотвращает зависание приложения на экране загрузки
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);